import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../contexts/useAuth';
import {
    format,
    startOfMonth,
    endOfMonth,
    addMonths,
    subMonths,
    eachDayOfInterval,
    isToday,
    getYear,
    getDay,
    startOfWeek,
    endOfWeek,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    ChevronLeft,
    ChevronRight,
    Calendar,
    Send,
    Loader2,
    Clock,
    AlertTriangle,
} from 'lucide-react';
import { cn, dateDepuisUrl, hoursToDays, getStatusLabel, formatDate } from '../lib/utils';
import { Tooltip } from '../components/Tooltip';
import type { ApiResponse, TimeEntry as TimeEntryType } from '../types';

// Calcul de Pâques (algorithme de Meeus/Jones/Butcher)
function getEasterDate(year: number): Date {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

// Jours fériés français
function getFrenchHolidays(year: number): Map<string, string> {
    const holidays = new Map<string, string>();

    holidays.set(`${year}-01-01`, "Jour de l'An");
    holidays.set(`${year}-05-01`, 'Fête du Travail');
    holidays.set(`${year}-05-08`, 'Victoire 1945');
    holidays.set(`${year}-07-14`, 'Fête Nationale');
    holidays.set(`${year}-08-15`, 'Assomption');
    holidays.set(`${year}-11-01`, 'Toussaint');
    holidays.set(`${year}-11-11`, 'Armistice 1918');
    holidays.set(`${year}-12-25`, 'Noël');

    const easter = getEasterDate(year);

    const easterMonday = new Date(easter);
    easterMonday.setDate(easter.getDate() + 1);
    holidays.set(format(easterMonday, 'yyyy-MM-dd'), 'Lundi de Pâques');

    const ascension = new Date(easter);
    ascension.setDate(easter.getDate() + 39);
    holidays.set(format(ascension, 'yyyy-MM-dd'), 'Ascension');

    const pentecostMonday = new Date(easter);
    pentecostMonday.setDate(easter.getDate() + 50);
    holidays.set(format(pentecostMonday, 'yyyy-MM-dd'), 'Lundi de Pentecôte');

    return holidays;
}

function isHoliday(date: Date): { isHoliday: boolean; name?: string } {
    const year = getYear(date);
    const holidays = getFrenchHolidays(year);
    const dateStr = format(date, 'yyyy-MM-dd');
    const holidayName = holidays.get(dateStr);
    return { isHoliday: !!holidayName, name: holidayName };
}

// Count working days in month (excluding weekends and holidays)
function getWorkingDaysInMonth(monthStart: Date, monthEnd: Date): number {
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    return days.filter((day) => {
        const dayOfWeek = getDay(day);
        const holiday = isHoliday(day);
        return dayOfWeek !== 0 && dayOfWeek !== 6 && !holiday.isHoliday;
    }).length;
}

export function MonthView() {
    const navigate = useNavigate();
    const api = useApi();
    const queryClient = useQueryClient();
    const { date: dateParam } = useParams();
    const [selectedMonth, setSelectedMonth] = useState(() => dateDepuisUrl(dateParam));
    const { isAuthenticated, isLoading: authLoading } = useAuth();

    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);

    // Calendar grid: start from Monday of the week containing the 1st
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    // All days of the actual month
    const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Working days count
    const workingDaysTarget = getWorkingDaysInMonth(monthStart, monthEnd);

    // Fetch month entries
    const { data: entriesData, isLoading } = useQuery({
        queryKey: ['time-entries', 'month', format(monthStart, 'yyyy-MM-dd')],
        queryFn: () =>
            api.get<
                ApiResponse<{
                    entries: TimeEntryType[];
                    monthStart: string;
                    monthEnd: string;
                    dailyTotals: Record<string, number>;
                    monthTotal: number;
                }>
            >(`/api/time-entries/month/${format(monthStart, 'yyyy-MM-dd')}`, {
                myEntries: true,
            }),
        enabled: isAuthenticated && !authLoading,
    });

    const entries = entriesData?.data?.entries || [];

    // Submit all drafts mutation
    const submitAllMutation = useMutation({
        mutationFn: () => {
            const draftIds = entries.filter((e) => e.status === 'draft').map((e) => e.id);
            return api.post('/api/time-entries/submit-batch', { entryIds: draftIds });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        },
    });

    const goToPreviousMonth = () => setSelectedMonth(subMonths(selectedMonth, 1));
    const goToNextMonth = () => setSelectedMonth(addMonths(selectedMonth, 1));
    const goToToday = () => setSelectedMonth(new Date());

    // Group entries by date for the whole month
    const entriesByDate = new Map<
        string,
        {
            entries: TimeEntryType[];
            totalHours: number;
            hasDrafts: boolean;
            hasRejected: boolean;
        }
    >();

    monthDays.forEach((day) => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const dayEntries = entries.filter(
            (e) => format(new Date(e.entryDate), 'yyyy-MM-dd') === dayStr
        );
        const totalHours = dayEntries.reduce((sum, e) => sum + e.hours, 0);
        entriesByDate.set(dayStr, {
            entries: dayEntries,
            totalHours,
            hasDrafts: dayEntries.some((e) => e.status === 'draft'),
            hasRejected: dayEntries.some((e) => e.status === 'rejected'),
        });
    });

    const monthTotalHours = Array.from(entriesByDate.values()).reduce(
        (sum, d) => sum + d.totalHours,
        0
    );
    const monthTotalDays = hoursToDays(monthTotalHours);
    const hasDraftsInMonth = Array.from(entriesByDate.values()).some((d) => d.hasDrafts);

    const weekDayHeaders = ['Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.', 'Dim.'];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={goToPreviousMonth}
                        className="p-2 hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="text-center">
                        <h1 className="text-xl font-bold text-[var(--color-text)] capitalize">
                            {format(monthStart, 'MMMM yyyy', { locale: fr })}
                        </h1>
                    </div>

                    <button
                        onClick={goToNextMonth}
                        className="p-2 hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={goToToday}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                    >
                        <Calendar className="w-4 h-4" />
                        Aujourd'hui
                    </button>

                    {hasDraftsInMonth && (
                        <button
                            onClick={() => submitAllMutation.mutate()}
                            disabled={submitAllMutation.isPending}
                            className="btn-primary"
                        >
                            {submitAllMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                            Soumettre le mois
                        </button>
                    )}
                </div>
            </div>

            {submitAllMutation.isError && (
                <p role="alert" className="text-sm text-[var(--color-error)]">
                    Le mois n'a pas pu être soumis : {submitAllMutation.error.message}
                </p>
            )}

            {/* Month Summary */}
            <div className="card">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-[var(--color-text-muted)]" />
                        <span className="text-[var(--color-text-secondary)]">Total du mois</span>
                    </div>
                    <div className="text-right">
                        <span
                            className={cn(
                                'text-2xl font-bold',
                                monthTotalDays >= workingDaysTarget
                                    ? 'text-[var(--color-success)]'
                                    : monthTotalDays >= workingDaysTarget * 0.8
                                        ? 'text-[var(--color-warning)]'
                                        : 'text-[var(--color-text)]'
                            )}
                        >
                            {monthTotalDays}j
                        </span>
                        <span className="text-[var(--color-text-muted)] text-sm ml-2">
                            / {workingDaysTarget}j
                        </span>
                    </div>
                </div>
                {monthTotalDays < workingDaysTarget && monthTotalDays > 0 && (
                    <div className="mt-3">
                        <div className="h-2 bg-[var(--color-bg)] rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[var(--color-primary)] rounded-full transition-all duration-300"
                                style={{
                                    width: `${Math.min((monthTotalDays / workingDaysTarget) * 100, 100)}%`,
                                }}
                            />
                        </div>
                        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                            Il manque{' '}
                            {Math.round((workingDaysTarget - monthTotalDays) * 100) / 100}j pour
                            atteindre le mois standard
                        </p>
                    </div>
                )}
            </div>

            {/* Calendar Grid */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : (
                <div>
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                        {weekDayHeaders.map((day) => (
                            <div
                                key={day}
                                className="text-center text-xs font-medium text-[var(--color-text-muted)] py-2"
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Calendar cells */}
                    <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((day) => {
                            const dayStr = format(day, 'yyyy-MM-dd');
                            const isCurrentMonth =
                                day.getMonth() === monthStart.getMonth() &&
                                day.getFullYear() === monthStart.getFullYear();
                            const dayData = entriesByDate.get(dayStr);

                            return (
                                <MonthDayCell
                                    key={dayStr}
                                    date={day}
                                    entries={dayData?.entries || []}
                                    totalHours={dayData?.totalHours || 0}
                                    hasDrafts={dayData?.hasDrafts || false}
                                    hasRejected={dayData?.hasRejected || false}
                                    isCurrentMonth={isCurrentMonth}
                                    onClick={() => {
                                        if (isCurrentMonth) {
                                            navigate(`/time-entry/${dayStr}`);
                                        }
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-bg-hover)]" />
                    <span className="text-[var(--color-text-secondary)]">Brouillon</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-warning)]" />
                    <span className="text-[var(--color-text-secondary)]">Soumis</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-success)]" />
                    <span className="text-[var(--color-text-secondary)]">Validé</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-error)]" />
                    <span className="text-[var(--color-text-secondary)]">Rejeté</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[var(--color-primary)]" />
                    <span className="text-[var(--color-text-secondary)]">Facturé</span>
                </div>
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-[var(--color-border)]">
                    <AlertTriangle className="w-3 h-3 text-orange-500" />
                    <span className="text-[var(--color-text-secondary)]">
                        Week-end/Férié ou &gt;1j
                    </span>
                </div>
            </div>
        </div>
    );
}

interface MonthDayCellProps {
    date: Date;
    entries: TimeEntryType[];
    totalHours: number;
    hasDrafts: boolean;
    hasRejected: boolean;
    isCurrentMonth: boolean;
    onClick: () => void;
}

function MonthDayCell({
    date,
    entries,
    totalHours,
    hasDrafts,
    hasRejected,
    isCurrentMonth,
    onClick,
}: MonthDayCellProps) {
    const isCurrentDay = isToday(date);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const totalDays = hoursToDays(totalHours);
    const holidayInfo = isHoliday(date);
    const isSpecialDay = isWeekend || holidayInfo.isHoliday;

    const hasWarning = (isSpecialDay && entries.length > 0) || totalDays > 1;

    if (!isCurrentMonth) {
        return <div className="min-h-[90px] rounded-lg bg-transparent" />;
    }

    return (
        <button
            onClick={onClick}
            className={cn(
                'min-h-[90px] rounded-lg border p-2 text-left transition-all hover:shadow-md relative flex flex-col',
                'border-[var(--color-border)] bg-[var(--color-surface)]',
                isCurrentDay &&
                'border-[var(--color-primary)] ring-2 ring-[var(--color-primary-light)]',
                isSpecialDay && 'bg-[var(--color-bg)]',
                hasWarning && 'border-orange-400 ring-1 ring-orange-200'
            )}
        >
            {/* Warning indicator */}
            {hasWarning && (
                <div className="absolute -top-1.5 -right-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                </div>
            )}

            {/* Day header */}
            <div className="flex items-center justify-between mb-1">
                <span
                    className={cn(
                        'text-sm font-semibold',
                        isCurrentDay
                            ? 'text-[var(--color-primary)]'
                            : isSpecialDay
                                ? 'text-orange-500'
                                : 'text-[var(--color-text)]'
                    )}
                >
                    {format(date, 'd')}
                </span>
                {totalDays > 0 && (
                    <span
                        className={cn(
                            'text-xs font-semibold',
                            totalDays > 1
                                ? 'text-orange-500'
                                : totalDays >= 1
                                    ? 'text-[var(--color-success)]'
                                    : 'text-[var(--color-warning)]'
                        )}
                    >
                        {totalDays}j
                    </span>
                )}
            </div>

            {/* Entries preview */}
            <div className="flex-1">
                {entries.length > 0 ? (
                    <div className="space-y-0.5">
                        {entries.slice(0, 2).map((entry) => (
                            <Tooltip key={entry.id} content={<ProjectEntryTooltip entry={entry} />}>
                                <div className="flex items-center gap-1 text-[10px]">
                                    <span
                                        className={cn(
                                            'w-1.5 h-1.5 rounded-full flex-shrink-0',
                                            entry.status === 'draft' && 'bg-[var(--color-bg-hover)]',
                                            entry.status === 'submitted' &&
                                            'bg-[var(--color-warning)]',
                                            entry.status === 'approved' &&
                                            'bg-[var(--color-success)]',
                                            entry.status === 'rejected' && 'bg-[var(--color-error)]',
                                            entry.status === 'billed' && 'bg-[var(--color-primary)]'
                                        )}
                                    />
                                    <span className="text-[var(--color-text-secondary)] truncate flex-1">
                                        {entry.projectCode}
                                    </span>
                                    <span className="text-[var(--color-text-muted)]">
                                        {hoursToDays(entry.hours)}j
                                    </span>
                                </div>
                            </Tooltip>
                        ))}
                        {entries.length > 2 && (
                            <p className="text-[10px] text-[var(--color-text-muted)]">
                                +{entries.length - 2}
                            </p>
                        )}
                    </div>
                ) : (
                    <p
                        className={cn(
                            'text-[10px] italic',
                            isSpecialDay
                                ? 'text-orange-500'
                                : 'text-[var(--color-text-muted)]'
                        )}
                    >
                        {holidayInfo.isHoliday
                            ? holidayInfo.name
                            : isWeekend
                                ? 'Week-end'
                                : ''}
                    </p>
                )}
            </div>

            {/* Status indicators */}
            {(hasDrafts || hasRejected) && (
                <div className="mt-1 pt-1 border-t border-[var(--color-border)] flex gap-1">
                    {hasDrafts && (
                        <span className="text-[10px] text-[var(--color-text-secondary)]">
                            À soumettre
                        </span>
                    )}
                    {hasRejected && (
                        <span className="text-[10px] text-[var(--color-error)]">À corriger</span>
                    )}
                </div>
            )}
        </button>
    );
}

function ProjectEntryTooltip({ entry }: { entry: TimeEntryType }) {
    return (
        <div className="space-y-1">
            <p className="font-semibold text-[var(--color-text)]">
                {entry.projectName || entry.projectCode || 'Projet'}
            </p>
            {entry.clientName && (
                <p className="text-[var(--color-text-secondary)]">{entry.clientName}</p>
            )}
            <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
                <span>{hoursToDays(entry.hours)}j ({entry.hours}h)</span>
                <span>·</span>
                <span>{getStatusLabel(entry.status)}</span>
            </div>
            {entry.taskType && (
                <p className="text-[var(--color-text-muted)]">{entry.taskType}</p>
            )}
            {entry.description && (
                <p className="text-[var(--color-text-secondary)] italic">{entry.description}</p>
            )}
            {entry.status === 'rejected' && entry.rejectionReason && (
                <p className="text-[var(--color-error)]">Motif: {entry.rejectionReason}</p>
            )}
            {entry.approvedAt && entry.approverName && (
                <p className="text-[var(--color-text-muted)]">
                    Validé par {entry.approverName} le {formatDate(entry.approvedAt)}
                </p>
            )}
        </div>
    );
}
